/**
 * The live feed client.
 *
 * Wraps EventSource with the three things the naive version gets wrong on venue
 * wifi: it reconnects, it resumes rather than restarting, and it stops when
 * nobody is watching.
 *
 * Resumption is the important one. The Worker sends `id:` on every media event,
 * and on reconnect the browser replays the last one it saw. A wall that dropped
 * its connection during the speeches must not come back showing only the photos
 * taken since — the whole point of the feed is that a projector is never stale.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { Media } from "@shared";

import { readSessionToken } from "./session-token.js";

export type FeedStatus = "connecting" | "live" | "reconnecting" | "closed";

export interface FeedState {
  readonly status: FeedStatus;
  readonly media: readonly Media[];
  readonly lastEventId: string | null;
  /**
   * Ids the server has withdrawn since the stream opened — deleted, rejected or
   * hidden by the host. A caller that renders media it fetched some other way
   * (the wall's first paint comes from D1) must filter by this too: dropping
   * an id from `media` cannot remove a copy the feed never held.
   */
  readonly removed: ReadonlySet<string>;
}

interface Options {
  /** Slug, or null to disconnect. */
  readonly slug: string | null;
  /** Proof the caller holds: a wall token, a guest code, or neither for a host. */
  readonly token?: string;
  readonly guestCode?: string;
  /** Called once per new or changed item, for arrival animations. */
  readonly onMedia?: (media: Media) => void;
}

const MAX_ITEMS = 60;

export interface FeedQuery {
  readonly token?: string;
  /**
   * Clerk session token for the host-only signal channel. EventSource cannot
   * send an Authorization header, so the short-lived token travels in the query
   * string for this one connection.
   */
  readonly hostToken?: string;
  readonly guestCode?: string;
  /** The last media id this client saw. Sent to resume rather than replay. */
  readonly lastEventId?: string | null;
  /** Ask only for "something changed", not for the documents themselves. */
  readonly signalOnly?: boolean;
}

/**
 * Build the event-stream URL.
 *
 * Exported because this is the one piece of the feed with logic in it, and the
 * one worth testing: getting the resume parameter wrong is invisible until a
 * venue's Wi-Fi hiccups and every photograph on the wall re-announces itself.
 */
export function buildFeedUrl(slug: string, query: FeedQuery = {}): string {
  const params = new URLSearchParams();
  if (query.token) params.set("k", query.token);
  if (query.hostToken) params.set("hostToken", query.hostToken);
  if (query.guestCode) params.set("guestCode", query.guestCode);
  if (query.lastEventId) params.set("lastEventId", query.lastEventId);
  if (query.signalOnly) params.set("mode", "signal");
  const suffix = params.toString() ? `?${params}` : "";
  return `/api/live/${slug}${suffix}`;
}

/**
 * Run `onSignal` whenever anything changes on an event, without receiving it.
 *
 * The moderation queue's problem is that a photograph arriving as `pending` is
 * deliberately not on the wall, so the feed that a wall listens to will never
 * mention it. The host is left refreshing a page that does not change.
 *
 * Polling would work and is what a first implementation reaches for, but D1
 * charges rows read: a five-second poll on a screen someone is merely looking
 * at is 720 requests an hour, each reading a page of media. This asks the
 * Durable Object for a one-word "something changed" instead, and the refetch
 * happens once, when there is genuinely something to see.
 */
export function useArrivalSignal(slug: string | undefined, onSignal: () => void): void {
  const callback = useRef(onSignal);
  callback.current = onSignal;

  useEffect(() => {
    if (!slug) return;

    let source: EventSource | null = null;
    let cancelled = false;
    const onFrame = (): void => callback.current();

    // EventSource has no header support. Fetch the host's short-lived Clerk
    // token first, then put it on this one URL; without it the Worker rightly
    // answered the signal subscription with 401 and the queue never refreshed.
    const connect = async (): Promise<void> => {
      const hostToken = await readSessionToken();
      if (cancelled) return;

      source = new EventSource(
        buildFeedUrl(slug, { signalOnly: true, hostToken: hostToken ?? undefined }),
      );

      // Every frame means something changed, whether it is a new upload, a
      // moderation decision, or a deletion. The client refetches rather than
      // trying to apply a delta it was not sent.
      for (const event of ["incoming", "media", "updated", "removed"]) {
        source.addEventListener(event, onFrame as EventListener);
      }
    };

    void connect();

    return () => {
      cancelled = true;
      source?.close();
    };
  }, [slug]);
}

export function useLiveFeed({ slug, token, guestCode, onMedia }: Options): FeedState {
  const [status, setStatus] = useState<FeedStatus>("connecting");
  const [media, setMedia] = useState<Media[]>([]);
  const [lastEventId, setLastEventId] = useState<string | null>(null);
  const [removed, setRemoved] = useState<ReadonlySet<string>>(() => new Set());

  // Refs, not state: the EventSource identity and the arrival callback must not
  // be able to tear down and rebuild the connection.
  const sourceRef = useRef<EventSource | null>(null);
  const onMediaRef = useRef(onMedia);
  const retryRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The resume position, in a ref because the reconnect closure has to read it
  // without being recreated.
  const lastEventIdRef = useRef<string | null>(null);

  onMediaRef.current = onMedia;

  const apply = useCallback((next: Media) => {
    // An item can come back: rejected, then approved after all.
    setRemoved((previous) => {
      if (!previous.has(next.mediaId)) return previous;
      const copy = new Set(previous);
      copy.delete(next.mediaId);
      return copy;
    });
    setMedia((previous) => {
      const index = previous.findIndex((item) => item.mediaId === next.mediaId);
      if (index === -1) return [next, ...previous].slice(0, MAX_ITEMS);

      // A moderation decision changes an item in place. Replacing rather than
      // appending is what stops an approved photo appearing twice on the wall.
      const copy = previous.slice();
      copy[index] = next;
      return copy;
    });
    lastEventIdRef.current = next.mediaId;
    setLastEventId(next.mediaId);
  }, []);

  useEffect(() => {
    if (!slug) {
      setStatus("closed");
      return;
    }

    let disposed = false;

    const connect = (): void => {
      if (disposed) return;

      // The resume position goes in the query string, not the Last-Event-ID
      // header. The browser only sends that header for reconnects *it*
      // initiates; this client closes the stream and constructs a new
      // EventSource, which carries no such state. Without the parameter the
      // server replays its whole ring buffer and every photograph re-announces
      // itself as newly arrived — the exact moment a wall is most visible.
      const source = new EventSource(buildFeedUrl(slug, { token, guestCode, lastEventId: lastEventIdRef.current }));
      sourceRef.current = source;

      source.onopen = () => {
        retryRef.current = 0;
        setStatus("live");
      };

      source.onmessage = () => {
        /* the named handlers below carry the payload */
      };

      const onFrame = (event: MessageEvent<string>): void => {
        try {
          const parsed = JSON.parse(event.data) as {
            type?: string;
            media?: Media;
            mediaId?: string;
          };

          if (parsed.media) {
            apply(parsed.media);
            onMediaRef.current?.(parsed.media);
            return;
          }

          if (parsed.type === "removed" && parsed.mediaId) {
            const gone = parsed.mediaId;
            setMedia((previous) => previous.filter((item) => item.mediaId !== gone));
            setRemoved((previous) => (previous.has(gone) ? previous : new Set(previous).add(gone)));
            lastEventIdRef.current = parsed.mediaId;
            setLastEventId(parsed.mediaId);
          }
        } catch {
          // A truncated frame is not worth tearing the connection down for; the
          // next event will carry the same information again.
        }
      };

      source.addEventListener("media", onFrame as EventListener);
      source.addEventListener("updated", onFrame as EventListener);
      source.addEventListener("removed", onFrame as EventListener);

      source.onerror = () => {
        source.close();
        if (disposed) return;

        // The browser distinguishes the two failures for us, and the difference
        // decides whether retrying can ever help.
        //
        //   CLOSED  — the server answered with a non-2xx status. Per the HTML
        //             spec that fails the connection permanently. This is an
        //             authorisation or routing problem; a wall token will not
        //             become valid by asking again, so retrying is pure load.
        //   CONNECTING — a transport hiccup. The browser would reconnect on its
        //             own, and we do too, with backoff.
        if (source.readyState === EventSource.CLOSED) {
          setStatus("closed");
          return;
        }

        setStatus("reconnecting");
        // Exponential backoff, capped. A projector that retries every second
        // forever turns a transient outage into a sustained load on the Worker.
        retryRef.current = Math.min(retryRef.current + 1, 6);
        const delay = Math.min(30_000, 1000 * 2 ** (retryRef.current - 1));
        timerRef.current = setTimeout(connect, delay);
      };
    };

    setStatus("connecting");
    setMedia([]);
    setRemoved(new Set());
    setLastEventId(null);
    lastEventIdRef.current = null;
    connect();

    return () => {
      disposed = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      sourceRef.current?.close();
      sourceRef.current = null;
    };
  }, [slug, token, guestCode, apply]);

  return { status, media, lastEventId, removed };
}
